import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import * as moment from 'moment-timezone';
import { HttpService } from './http.service';
import { SharedService } from './shared.service';
import { DictionaryService } from './dictionary.service';
import { GridService } from './grid.service';
import { UserService } from './user.service';

@Injectable()
export class ReportsService {
  public timezones: Array<any>;
  public selectedTimezone: any;
  public dimensions_v1: Array<any>;
  public dimensions_v2: Array<any>;
  public pseudoDimensions_v1: Array<any>;
  public pseudoDimensions_v2: Array<any>;
  public adminDimensions_v1: Array<any>;
  public adminDimensions_v2: Array<any>;
  public adminPseudoDimensions_v1: Array<any>;
  public adminPseudoDimensions_v2: Array<any>;
  public generatedDimensionOptions: Array<any>;
  public dayDimensionId: number;
  public hourDimensionId: number;
  public queriesLimit: number;
  public numOfRecentReports: number;
  public defaultReports: Array<any>;
  public customReports: Array<any>;
  public recentReports: Array<any>;
  public graphColors: Array<any>;
  public graphDefaults: Array<any>;
  public graphProperties: Array<any>;
  public graphMaxItemsForSingleLines: number;
  public graphMaxLoadFails: number;
  public isReportSentByEmail: boolean;
  public labsDaysLimit: number;
  public legacyAllDaysLimit: number;
  public legacyByDaysLimit: number;
  public defaultDaysLimit: number;

  constructor(
    public httpService: HttpService,
    public sharedService: SharedService,
    public dictionaryService: DictionaryService,
    public userService: UserService,
    public gridService: GridService
  ) {
    this.timezones = this.initTimezones();
    this.selectedTimezone = localStorage.getItem('selectedTimezone')
      ? JSON.parse(localStorage.getItem('selectedTimezone'))
      : moment.tz.zone('EST');
    this.dimensions_v1 = [];
    this.dimensions_v2 = [];
    this.pseudoDimensions_v1 = [];
    this.pseudoDimensions_v2 = [];
    this.adminDimensions_v1 = [];
    this.adminDimensions_v2 = [];
    this.adminPseudoDimensions_v1 = [];
    this.adminPseudoDimensions_v2 = [];
    this.generatedDimensionOptions = ['dy', 'hr', 'st', 'dt', 'en'];
    this.dayDimensionId = 9;
    this.hourDimensionId = 10;
    this.queriesLimit = 1000;
    this.numOfRecentReports = 5;
    this.labsDaysLimit = 90;
    this.legacyAllDaysLimit = 90;
    this.legacyByDaysLimit = 90;
    this.defaultDaysLimit = 90;
    this.defaultReports = [];
    this.customReports = [];
    this.recentReports = [];
    if (localStorage.getItem('recentReports')) {
      this.recentReports = JSON.parse(localStorage.getItem('recentReports'));
    }
    this.graphColors = [
      { name: 'lightBlue', hex: '#3fa9f5' },
      { name: 'green', hex: '#79c843' },
      { name: 'orange', hex: '#ff921e' },
      { name: 'pink', hex: '#ff7aac' },
      { name: 'darkBlue', hex: '#1c75be' },
    ];

    this.graphDefaults = [];
    this.graphProperties = _.cloneDeep(this.graphDefaults);
    this.graphMaxItemsForSingleLines = 5;
    this.graphMaxLoadFails = 3;
    this.isReportSentByEmail = false;
    this.buildDefaultReports();
    localStorage.setItem('selectedTimezone', JSON.stringify(this.selectedTimezone));
  }

  initTimezones() {
    const timezones: Array<any> = [];
    let offset: number;
    moment.tz.names().forEach((timezone: string) => {
      offset = parseFloat(this.getTimezoneOffset(timezone, true).toString());
      if (offset === Math.round(offset)) {
        timezones.push({
          key: timezone,
          value: timezone + ' ' + this.getTimezoneOffset(timezone, false),
        });
      }
    });
    return timezones;
  }

  updateRecentReports(report: any) {
    let recentReports: Array<any> = [];
    let currReportId: number;
    const index: number = _.findIndex(recentReports, report);
    let trimIndex = 1;
    // Clone recent reports, and remove deleted reports
    for (let i = 0; i < this.recentReports.length; i++) {
      if (this.recentReports[i].id) {
        currReportId = parseInt(this.recentReports[i].id);
        if (
          _.find(this.customReports, function (o) {
            return parseInt(o.reportId) === currReportId;
          })
        ) {
          recentReports.push(this.recentReports[i]);
        }
      } else {
        recentReports.push(this.recentReports[i]);
      }
    }
    // Search for the last visited report in the recent reports array
    if (index > -1) {
      recentReports.splice(index, 1);
      trimIndex = index;
    }
    // Move all reports by one position
    for (let i: number = recentReports.length; i >= trimIndex; i--) {
      recentReports[i] = recentReports[i - 1];
    }
    // Save the last visited report as the first one in the array
    recentReports[0] = report;
    // Verify that there are no duplicates
    recentReports = _.uniqBy(recentReports, 'name');
    // Reset the actual array in use
    this.recentReports = [];
    // Pass a maximum of 5 reports to the saved array
    for (let i = 0; i < Math.min(recentReports.length, this.numOfRecentReports); i++) {
      this.recentReports[i] = recentReports[i];
    }
    // Emit a change event
    this.sharedService.sendMessage('RECENT_REPORTS_UPDATED');
    // Save the data in local storage
    localStorage.setItem('recentReports', JSON.stringify(this.recentReports));
  }

  getDefaultReportsNames(options: any = {}) {
    const names: Array<string> = _.map(this.defaultReports, 'name');
    for (let i = 0; i < names.length; i++) {
      names[i] = this.dictionaryService.get(names[i]);
      if (options.lowercase) {
        names[i] = names[i].toLowerCase();
      }
    }
    return names;
  }

  getCurrentDate(action: string = null, amount: any = null, offset: any = null, customTimezone: boolean = false) {
    let date: any;
    const tzOffset: number = customTimezone ? Number(this.getTimezoneOffset()) * 60 : this.sharedService.offsetEST;
    switch (action) {
      case 'subtract':
        date = moment().utc().utcOffset(tzOffset).subtract(amount, offset);
        break;
      case 'startThisMonth':
        date = moment().utc().utcOffset(tzOffset).startOf('month');
        break;
      case 'startLastMonth':
        date = moment().utc().utcOffset(tzOffset).subtract(1, 'month').startOf('month');
        break;
      case 'endLastMonth':
        date = moment().utc().utcOffset(tzOffset).subtract(1, 'month').endOf('month');
        break;
      default:
        date = moment().utc().utcOffset(tzOffset);
    }
    return new Date(date.year(), date.month(), date.date());
  }

  isValidDate(date: Date, customTimezone: boolean) {
    const startOfDay: Date = new Date(date.setHours(0, 0, 0, 0));
    const diffFromNow: number = moment
      .duration(moment(startOfDay).diff(this.getCurrentDate(null, null, null, customTimezone)))
      .asDays();
    return diffFromNow <= 0;
  }

  setTimezone(timezoneName: any, forcedChange: boolean = false) {
    if (this.selectedTimezone.name !== timezoneName) {
      this.selectedTimezone = moment.tz.zone(timezoneName);
      this.sharedService.showFlashMessage(this.dictionaryService.get('TIMEZONE_CHANGED_SUCCESSFULLY'), 'success');
      localStorage.setItem('selectedTimezone', JSON.stringify(this.selectedTimezone));
      if (!forcedChange) {
        this.sharedService.sendMessage('TIMEZONE_CHANGE');
      }
    }
  }

  getTimezoneOffset(timezone: string = null, asNumber: boolean = true) {
    let offset = 0;
    let label: string;
    if (timezone === null) {
      timezone = this.selectedTimezone.name;
    }
    offset = moment.tz(new Date(), timezone)._offset / 60;
    if (asNumber) {
      return offset;
    } else {
      label = '(GMT';
      label += offset > 0 ? '+' : '-';
      label += this.sharedService.padNumber(Math.abs(Math.round(offset))) + ':';
      label += this.sharedService.padNumber((offset - Math.floor(offset)) * 60);
      label += ')';
      return label;
    }
  }

  buildDefaultReports(): void {
    this.defaultReports = [
      { id: 1, primaryKey: 'supply', name: 'ALL_SUPPLY', link: '/supply/all', d: [1], admin: false },
      { id: 2, primaryKey: 'supply', name: 'SUPPLY_BY_DEMAND', link: '/supply/by-demand', d: [1, 2], admin: false },
      { id: 3, primaryKey: 'supply', name: 'SUPPLY_BY_DAY', link: '/supply/by-day', d: [1, 9], admin: false },
      { id: 4, primaryKey: 'supply', name: 'SUPPLY_BY_DOMAIN', link: '/supply/by-domain', d: [1, 3], admin: false },
      { id: 5, primaryKey: 'demand', name: 'ALL_DEMAND', link: '/demand/all', d: [2], admin: false },
      { id: 6, primaryKey: 'demand', name: 'DEMAND_BY_SUPPLY', link: '/demand/by-supply', d: [2, 1], admin: false },
      { id: 7, primaryKey: 'demand', name: 'DEMAND_BY_DAY', link: '/demand/by-day', d: [2, 9], admin: false },
      { id: 8, primaryKey: 'demand', name: 'DEMAND_BY_DOMAIN', link: '/demand/by-domain', d: [2, 3], admin: false },
    ];
    if (['publisher', 'cedatopub'].includes(this.userService.userData.userType)) {
      if (this.userService.userData.isHB) {
        this.defaultReports.push({
          id: 20,
          primaryKey: 'cedatopub',
          name: 'DOMAIN',
          link: '/domain/all',
          d: [3],
          labsVer: 2,
          admin: false,
        });
      } else {
        this.defaultReports.push({
          id: 20,
          primaryKey: 'cedatopub',
          name: 'ALL_INVENTORY',
          link: '/inventory/all',
          d: [1],
          labsVer: 2,
          admin: false,
        });
      }
    }
    if (this.userService.getFeatureState('content_reports')) {
      this.defaultReports.push({
        id: 30,
        primaryKey: 'content',
        name: 'CONTENT_REPORTS',
        link: '/content/reports',
        d: [26],
        labsVer: 2,
        admin: false,
      });
    }
  }

  buildGraphDefaults(labsVer = 1) {
    const settings: any = this.gridService.columnsDefaults;
    this.graphDefaults = this.gridService.buildMeasureColumns(labsVer).map((one) => {
      const props = _.find(settings, { key: one.actualKey });
      if (props) {
        return {
          key: one.actualKey,
          label: props.label,
          decimal: !!props.decimal,
        };
      } else {
        return false;
      }
    });
  }

  narrowDefaultReports() {
    let reportsArray: Array<any>;

    if (this.userService.isCedatoPublisher()) {
      reportsArray = _.filter(this.defaultReports, (one) => ['cedatopub', 'publisher_hb'].includes(one.primaryKey));
    } else if (this.userService.isPublisher()) {
      reportsArray = [];
    } else {
      reportsArray = _.filter(this.defaultReports, (one) => !['cedatopub', 'publisher_hb'].includes(one.primaryKey));
    }

    return reportsArray;
  }
}
